import { useState } from "react";
import { PrimaryButton } from "@normasteaching/norma-ui";
import {
  BonificoOrderDTO,
  getBonificoOrderErrorMessage,
} from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { hasBonificoInvoiceBeenIssued } from "../../ProductService/BonificoOrder/bonificoBilling.ts";
import {
  invoiceItemDescriptionPlaceholderOf,
  standardInvoiceItemDescriptionLabel,
  standardInvoiceItemDescriptionOf,
} from "../../ProductService/BonificoOrder/invoiceItemDescription.ts";
import { useUpdateBonificoOrderInvoiceItemDescription } from "../../ProductService/BonificoOrder/useUpdateBonificoOrderInvoiceItemDescription.ts";
import { InvoiceItemDescriptionInput } from "./InvoiceItemDescriptionInput.tsx";

function CurrentDescription({ order }: { order: BonificoOrderDTO }) {
  if (order.invoiceItemDescription)
    return (
      <p className="break-words text-sm text-gray-700">
        {order.invoiceItemDescription}
      </p>
    );
  return (
    <p className="text-sm text-gray-400">
      {standardInvoiceItemDescriptionLabel(
        standardInvoiceItemDescriptionOf(order),
      )}
    </p>
  );
}

export function InvoiceItemDescriptionEditor({
  order,
  onSaved,
}: {
  order: BonificoOrderDTO;
  onSaved?: (order: BonificoOrderDTO) => void;
}) {
  const [savedOrder, setSavedOrder] = useState(order);
  const [draftDescription, setDraftDescription] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { updateInvoiceItemDescription, loading } =
    useUpdateBonificoOrderInvoiceItemDescription();

  if (hasBonificoInvoiceBeenIssued(savedOrder))
    return (
      <div className="flex flex-col gap-1">
        <CurrentDescription order={savedOrder} />
        <p className="text-xs text-gray-400">
          È la descrizione della fattura emessa: non è più modificabile.
        </p>
      </div>
    );

  const save = async () => {
    setError(null);
    try {
      const updated = await updateInvoiceItemDescription(
        savedOrder.orderId,
        draftDescription ?? "",
      );
      setSavedOrder(updated);
      setDraftDescription(null);
      onSaved?.(updated);
    } catch (e) {
      setError(
        getBonificoOrderErrorMessage(
          e,
          "Errore durante il salvataggio della descrizione. Riprova.",
        ),
      );
    }
  };

  if (draftDescription !== null)
    return (
      <div className="flex flex-col gap-2">
        <InvoiceItemDescriptionInput
          value={draftDescription}
          placeholder={invoiceItemDescriptionPlaceholderOf(savedOrder)}
          onChange={setDraftDescription}
        />
        <div className="flex items-center justify-end gap-3">
          {error && <span className="text-xs text-red-500">{error}</span>}
          <button
            type="button"
            onClick={() => {
              setDraftDescription(null);
              setError(null);
            }}
            className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 hover:bg-gray-50"
          >
            Annulla
          </button>
          <PrimaryButton
            label={loading ? "Salvataggio..." : "Salva descrizione"}
            onClick={save}
            disabled={loading}
          />
        </div>
      </div>
    );

  return (
    <div className="flex flex-col gap-2">
      <CurrentDescription order={savedOrder} />
      <button
        type="button"
        onClick={() =>
          setDraftDescription(savedOrder.invoiceItemDescription ?? "")
        }
        className="self-start text-sm text-blue-600 hover:underline"
      >
        {savedOrder.invoiceItemDescription
          ? "Modifica descrizione"
          : "Personalizza descrizione"}
      </button>
    </div>
  );
}
